import { useContext, useEffect, useState, useCallback } from "react"
import { PushNotifications, ActionPerformed, PushNotificationSchema } from "@capacitor/push-notifications"
import { useNavigate, useLocation } from "react-router-dom"
import { AlertContext } from "@/components/dashboard/contexts"

export function PushNotificationFooter({
  notification,
  removeAlert
}: {
  notification: PushNotificationSchema
  removeAlert: () => void
}) {
  const navigate = useNavigate()
  const callId = notification.data?.call_id

  if (!callId) {
    return null
  }

  return (
    <div className="w-full flex flex-col justify-end">
      {callId && (
        <div className="grow flex">
          <button
            onClick={() => {
              removeAlert()
              navigate(`/calls/${callId}`)
            }}
            className="mt-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-xl font-medium transition-all duration-200 hover:bg-blue-500"
          >
            View Call
          </button>
        </div>
      )}
    </div>
  )
}

export const usePushNotifications = () => {
  const [permissionStatus, setPermissionStatus] = useState<string | null>(null)
  const navigate = useNavigate()
  const { addAlert, removeAlert } = useContext(AlertContext)
  const location = useLocation()

  const handlePushNotificationActionPerformed = useCallback(
    (notification: ActionPerformed) => {
      console.log("Push notification action performed", notification)

      // Handle notification clicks (both when app is in background or closed)
      if (notification.actionId === "tap") {
        const data = notification.notification.data
        if (["call_success", "call_incomplete", "call_start"].includes(data.notification_type)) {
          navigate(`/calls/${data.call_id}`)
        }
      }
    },
    [navigate]
  )

  const handlePushNotificationReceived = useCallback(
    (notification: PushNotificationSchema) => {
      const callId = notification.data?.call_id
      const currentPath = location.pathname
      const isAlreadyOnCallPage = callId && currentPath === `/calls/${callId}`

      console.log("Push notification received", notification, location.pathname, callId, isAlreadyOnCallPage)

      // Only show notification if user is not already on this call's page
      if (!isAlreadyOnCallPage) {
        const id = Math.random().toString(36).substring(2, 15)

        addAlert({
          id,
          type: "info",
          children: <PushNotificationFooter notification={notification} removeAlert={() => removeAlert(id)} />,
          message: notification.body,
          className: "min-w-xs",
          rounded: true,
          withBorderAccent: true
        })
        console.log("Push notification received and added to alerts")
      } else {
        console.log("Push notification received but user is already on this call's page")
      }
    },
    [location, addAlert, removeAlert]
  )

  useEffect(() => {
    console.log("Initializing push notifications")

    // Request permission to use push notifications. iOS will prompt user and
    // return if they granted permission or not. Android will just grant without
    // prompting
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === "granted") {
        setPermissionStatus("granted")
        console.log("Push notifications granted")
      } else {
        setPermissionStatus("denied")
        console.log("Push notifications not granted")
      }
    })

    PushNotifications.addListener("pushNotificationActionPerformed", handlePushNotificationActionPerformed)
    PushNotifications.addListener("pushNotificationReceived", handlePushNotificationReceived)
    // Cleanup listeners when component unmounts or when handlers change
    return () => {
      // This will remove all listeners. If more specific listener removal is needed in the future,
      // the handles returned by addListener should be stored and used for removal.
      PushNotifications.removeAllListeners()
    }
  }, [handlePushNotificationActionPerformed, handlePushNotificationReceived])

  return permissionStatus
}
